import { useState } from 'react';
import type { UserProfile } from '../../shared/types.ts';

/**
 * カルテ。会話の最初におじさんが差し出す聞き取り票。
 * フォーム画面で足止めしないよう、会話の中にそのまま置き、いつでも飛ばせる。
 */
const STAGES: { value: NonNullable<UserProfile['stage']>; label: string }[] = [
  { value: 'thinking', label: 'まだ考えている' },
  { value: 'validating', label: 'アイデアを試している' },
  { value: 'preparing', label: '開業の準備中' },
  { value: 'launched', label: '始めたばかり' },
  { value: 'growing', label: '伸ばしたい' },
];

const EMPLOYMENTS: { value: NonNullable<UserProfile['employment']>; label: string }[] = [
  { value: 'employed', label: '会社員（副業なし）' },
  { value: 'side', label: '会社員＋副業中' },
  { value: 'fulltime', label: '専業' },
  { value: 'other', label: 'その他' },
];

const BUDGETS: { value: NonNullable<UserProfile['budget']>; label: string }[] = [
  { value: 'under50', label: '50万円未満' },
  { value: '50to300', label: '50〜300万円' },
  { value: '300to1000', label: '300〜1,000万円' },
  { value: 'over1000', label: '1,000万円以上' },
  { value: 'unknown', label: 'まだわからない' },
];

export function Intake({
  onSubmit,
  onSkip,
}: {
  onSubmit: (profile: UserProfile) => void;
  onSkip: () => void;
}) {
  const [profile, setProfile] = useState<UserProfile>({});

  const set = <K extends keyof UserProfile>(key: K, value: UserProfile[K]) =>
    setProfile((prev) => ({ ...prev, [key]: prev[key] === value ? undefined : value }));

  return (
    <div className="intake">
      <p className="intake__title">先に少しだけ教えてください</p>
      <p className="intake__hint">答えられるところだけで大丈夫です。飛ばしても相談はできます。</p>

      <div className="intake__group">
        <span className="intake__legend">いまの段階</span>
        <div className="chips">
          {STAGES.map((option) => (
            <button
              key={option.value}
              type="button"
              className="chip"
              aria-pressed={profile.stage === option.value}
              onClick={() => set('stage', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="intake__group">
        <span className="intake__legend">いまの働き方</span>
        <div className="chips">
          {EMPLOYMENTS.map((option) => (
            <button
              key={option.value}
              type="button"
              className="chip"
              aria-pressed={profile.employment === option.value}
              onClick={() => set('employment', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="intake__group">
        <span className="intake__legend">用意できる資金</span>
        <div className="chips">
          {BUDGETS.map((option) => (
            <button
              key={option.value}
              type="button"
              className="chip"
              aria-pressed={profile.budget === option.value}
              onClick={() => set('budget', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="intake__group">
        <label className="intake__legend" htmlFor="intake-field">
          やりたいこと・業種
        </label>
        <input
          id="intake-field"
          className="intake__field"
          value={profile.field ?? ''}
          placeholder="例: パン屋、Webデザインの受託、業務用SaaS"
          onChange={(e) => setProfile((prev) => ({ ...prev, field: e.target.value }))}
        />
      </div>

      <div className="intake__actions">
        <button type="button" className="send" onClick={() => onSubmit(profile)}>
          これで話す
        </button>
        <button type="button" className="ghost-button" onClick={onSkip}>
          飛ばす
        </button>
      </div>
    </div>
  );
}
